import { create } from 'zustand';
import { AllTranscriptsProps } from '@/types';
import { getConversationId } from '@/utils/getTranscriptId';

interface ConversationsState {
  completedConversations: AllTranscriptsProps[];
  incompleteConversations: AllTranscriptsProps[];
  screenedOutConversations: AllTranscriptsProps[];
  selectedTranscript: AllTranscriptsProps | null;
  completedTotalPages: number;
  incompleteTotalPages: number;
  screenedOutTotalPages: number;
  setCompletedConversations: (conversations: AllTranscriptsProps[]) => void;
  setIncompleteConversations: (conversations: AllTranscriptsProps[]) => void;
  setScreenedOutConversations: (conversations: AllTranscriptsProps[]) => void;
  setSelectedTranscript: (transcript: AllTranscriptsProps | null) => void;
  setCompletedTotalPages: (totalPages: number) => void;
  setIncompleteTotalPages: (totalPages: number) => void;
  setScreenedOutTotalPages: (totalPages: number) => void;
  getTranscriptById: (id: string) => AllTranscriptsProps | null;
  getPrevTranscript: (
    currentId: string,
    allConversations?: AllTranscriptsProps[],
  ) => AllTranscriptsProps | null;
  getNextTranscript: (
    currentId: string,
    allConversations?: AllTranscriptsProps[],
  ) => AllTranscriptsProps | null;
}

const useConversationsStore = create<ConversationsState>((set, get) => ({
  completedConversations: [],
  incompleteConversations: [],
  screenedOutConversations: [],
  selectedTranscript: null,
  completedTotalPages: 0,
  incompleteTotalPages: 0,
  screenedOutTotalPages: 0,

  setCompletedConversations: (conversations) => {
    set({ completedConversations: conversations });
  },

  setIncompleteConversations: (conversations) => {
    set((prev) => ({ ...prev, incompleteConversations: conversations }));
  },

  setScreenedOutConversations: (conversations) => {
    set((prev) => ({ ...prev, screenedOutConversations: conversations }));
  },

  setSelectedTranscript: (transcript) => {
    set({ selectedTranscript: transcript });
  },

  setCompletedTotalPages: (totalPages) => {
    set({ completedTotalPages: totalPages });
  },

  setIncompleteTotalPages: (totalPages) => {
    set({ incompleteTotalPages: totalPages });
  },

  setScreenedOutTotalPages: (totalPages) => {
    set({ screenedOutTotalPages: totalPages });
  },

  getTranscriptById: (id) => {
    const { completedConversations, incompleteConversations, screenedOutConversations } = get();

    const completedMatch = completedConversations.find((conv) => getConversationId(conv) === id);
    if (completedMatch) return completedMatch;

    const incompleteMatch = incompleteConversations.find((conv) => conv._id === id);
    if (incompleteMatch) return incompleteMatch;

    const screenedOutMatch = screenedOutConversations.find((conv) => conv._id === id);
    return screenedOutMatch || null;
  },

  getPrevTranscript: (currentId, allConversations) => {
    if (!allConversations || allConversations.length === 0) return null;

    const currentIndex = allConversations.findIndex(
      (conv) => getConversationId(conv) === currentId,
    );
    if (currentIndex <= 0) return null;

    return allConversations[currentIndex - 1];
  },

  getNextTranscript: (currentId, allConversations) => {
    if (!allConversations || allConversations.length === 0) return null;

    const currentIndex = allConversations.findIndex(
      (conv) => getConversationId(conv) === currentId,
    );
    if (currentIndex === -1 || currentIndex >= allConversations.length - 1) return null;

    return allConversations[currentIndex + 1];
  },
}));

export default useConversationsStore;
